import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";
import { Box, FormHelperText, TextField, Popover, Button } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { format, setHours, setMinutes } from "date-fns";

import "./TimePicker.scss";

function ScrollableColumn({ items, selectedValue, onSelect }) {
  const scrollRef = useRef(null);
  const itemRefs = useRef({});

  useEffect(() => {
    if (scrollRef.current && selectedValue && itemRefs.current[selectedValue]) {
      itemRefs.current[selectedValue].scrollIntoView({
        block: "center",
        behavior: "instant",
      });
    }
  }, [selectedValue]);

  const setItemRef = (value) => (el) => {
    itemRefs.current[value] = el;
  };

  return (
    <div className="time-picker-column">
      <div className="time-picker-column-scroll" ref={scrollRef}>
        {items.map((item) => (
          <div
            key={item.value}
            ref={setItemRef(item.value)}
            className={`time-picker-cell ${selectedValue === item.value ? "selected" : ""}`}
            onClick={() => onSelect(item.value)}
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomTimePicker({
  name,
  label,
  helperText,
  sx,
  placeholder,
  value: propValue,
  onChange: propOnChange,
  error: propError,
  size = "medium",
  variant = "outlined",
  disabled,
  width,
  minutesStep = 1,
  showIcon = true,
  ...other
}) {
  const formContext = useFormContext();
  const isControlled = propValue !== undefined && propOnChange !== undefined;
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempTime, setTempTime] = useState(null);

  const handleClick = (event, currentValue) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      // Initialize tempTime with current value when opening
      if (currentValue) {
        setTempTime(currentValue);
      } else {
        // Set default time if no value exists
        const defaultTime = new Date();
        defaultTime.setHours(12);
        defaultTime.setMinutes(0);
        setTempTime(defaultTime);
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOkay = (fieldOnChange) => {
    if (tempTime) {
      if (fieldOnChange) {
        fieldOnChange(tempTime);
      }
      if (propOnChange) {
        propOnChange(tempTime);
      }
    }
    handleClose();
  };

  const open = Boolean(anchorEl);

  const generateHours = () => {
    const hours = [];
    for (let i = 1; i <= 12; i += 1) {
      hours.push({ value: i, label: i.toString().padStart(2, "0") });
    }
    return hours;
  };

  const generateMinutes = () => {
    const minutes = [];
    for (let i = 0; i < 60; i += minutesStep) {
      minutes.push({ value: i, label: i.toString().padStart(2, "0") });
    }
    return minutes;
  };

  const ampmOptions = [
    { value: "AM", label: "AM" },
    { value: "PM", label: "PM" },
  ];

  const getTimeComponents = (date) => {
    if (!date) {
      return { hour: 12, minute: 0, ampm: "AM" };
    }
    const hours = date.getHours();
    const minutes = date.getMinutes();
    let hour = hours;
    if (hours === 0) {
      hour = 12;
    } else if (hours > 12) {
      hour = hours - 12;
    }
    const ampm = hours >= 12 ? "PM" : "AM";
    return { hour, minute: minutes, ampm };
  };

  const createTimeFromComponents = (hour, minute, ampm) => {
    let hours = hour;
    if (ampm === "PM" && hour !== 12) {
      hours = hour + 12;
    } else if (ampm === "AM" && hour === 12) {
      hours = 0;
    }
    const date = new Date();
    return setMinutes(setHours(date, hours), minute);
  };

  const renderTextField = (field = {}, fieldError) => {
    const currentValue = isControlled ? propValue : field.value;
    const displayValue = currentValue ? format(currentValue, "hh:mm aa") : "";
    const activeTime = tempTime || currentValue;
    const timeComponents = getTimeComponents(activeTime);

    return (
      <div
        className={`w-100p time-picker-container time-picker-${size} ${propError || fieldError ? "has-error" : ""} ${disabled ? "is-disabled" : ""}`}
      >
        {label && (
          <Box shrink htmlFor={name}>
            {label}
          </Box>
        )}
        <TextField
          {...other}
          id={name}
          name={name}
          size={size}
          variant={variant}
          disabled={disabled}
          error={!!fieldError}
          value={displayValue}
          placeholder={placeholder || "hh:mm aa"}
          onClick={(e) => handleClick(e, currentValue)}
          sx={{ width, ...sx }}
          InputProps={{
            readOnly: true,
            endAdornment: showIcon ? <AccessTimeOutlinedIcon className="time-icon" /> : null,
          }}
        />
        {(fieldError || helperText) && (
          <FormHelperText className={`custom-helper-text ${fieldError ? "error-helper-text" : ""}`}>
            {fieldError ? fieldError.message : helperText}
          </FormHelperText>
        )}

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          className="time-picker-popover"
          PaperProps={{
            className: "time-picker-paper",
          }}
        >
          <div className="time-picker-content">
            <div className="time-picker-grid">
              <ScrollableColumn
                items={generateHours()}
                selectedValue={timeComponents.hour}
                onSelect={(hour) => {
                  const newTime = createTimeFromComponents(hour, timeComponents.minute, timeComponents.ampm);
                  setTempTime(newTime);
                }}
              />
              <ScrollableColumn
                items={generateMinutes()}
                selectedValue={timeComponents.minute}
                onSelect={(minute) => {
                  const newTime = createTimeFromComponents(timeComponents.hour, minute, timeComponents.ampm);
                  setTempTime(newTime);
                }}
              />
              <ScrollableColumn
                items={ampmOptions}
                selectedValue={timeComponents.ampm}
                onSelect={(ampm) => {
                  const newTime = createTimeFromComponents(timeComponents.hour, timeComponents.minute, ampm);
                  setTempTime(newTime);
                }}
              />
            </div>
            <div className="time-picker-actions">
              <Button className="time-picker-okay-btn" onClick={() => handleOkay(field?.onChange)}>
                Okay
              </Button>
            </div>
          </div>
        </Popover>
      </div>
    );
  };

  if (formContext && !isControlled) {
    return (
      <Controller
        name={name}
        control={formContext.control}
        render={({ field, fieldState: { error } }) => renderTextField(field, error)}
      />
    );
  }

  return renderTextField({ value: propValue, onChange: propOnChange }, propError ? { message: helperText } : null);
}

CustomTimePicker.propTypes = {
  name: PropTypes.string.isRequired,
  sx: PropTypes.shape({}),
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  onChange: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  minutesStep: PropTypes.number,
  showIcon: PropTypes.bool,
};

CustomTimePicker.defaultProps = {
  helperText: "",
  sx: {},
  placeholder: "",
  value: undefined,
  onChange: undefined,
  error: false,
  size: "medium",
  disabled: false,
  variant: "outlined",
  minutesStep: 1,
  showIcon: true,
};

export default CustomTimePicker;
